import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { FileSpreadsheet, Download, Upload, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

export const BulkImportModal = ({
  open,
  onOpenChange,
  importStatus,
  setImportStatus,
  importFile,
  importPreview,
  importErrors,
  importing,
  onFileUpload,
  onDownloadTemplate,
  onBulkImport
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Bulk Import Leave Records
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to import multiple leave records at once
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Download Template */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">Download Template</p>
              <p className="text-sm text-slate-500">Use this template format for importing</p>
            </div>
            <Button variant="outline" onClick={onDownloadTemplate} className="gap-2">
              <Download className="w-4 h-4" />
              Download CSV
            </Button>
          </div>

          {/* Import Status Selection */}
          <div className="space-y-2">
            <Label>Import Status</Label>
            <Select value={importStatus} onValueChange={setImportStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="approved">Approved (Direct approval)</SelectItem>
                <SelectItem value="pending">Pending (Requires approval)</SelectItem>
                <SelectItem value="verified">Verified (Awaiting final approval)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Choose the status for imported leave records
            </p>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>Upload File</Label>
            <Input
              type="file"
              accept=".csv"
              onChange={onFileUpload}
              className="cursor-pointer"
            />
          </div>

          {/* Import Errors */}
          {importErrors.length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                <AlertCircle className="w-4 h-4" />
                Validation Errors ({importErrors.length})
              </div>
              <ul className="text-sm text-red-600 space-y-1">
                {importErrors.slice(0, 5).map((err) => (
                  <li key={`error-row-${err.row}`}>Row {err.row}: {err.errors.join(', ')}</li>
                ))}
                {importErrors.length > 5 && (
                  <li>... and {importErrors.length - 5} more errors</li>
                )}
              </ul>
            </div>
          )}

          {/* Preview Table */}
          {importPreview.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Preview ({importPreview.filter(r => r._valid).length} valid / {importPreview.length} total)</Label>
              </div>
              <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Employee</th>
                      <th className="px-3 py-2 text-left">Leave Type</th>
                      <th className="px-3 py-2 text-left">Dates</th>
                      <th className="px-3 py-2 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importPreview.map((row) => (
                      <tr key={`preview-row-${row._rowIndex}`} className={row._valid ? '' : 'bg-red-50'}>
                        <td className="px-3 py-2">
                          {row._valid ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500" />
                          ) : (
                            <Popover>
                              <PopoverTrigger>
                                <XCircle className="w-4 h-4 text-red-500 cursor-help" />
                              </PopoverTrigger>
                              <PopoverContent className="text-sm">
                                {row._errors?.join(', ')}
                              </PopoverContent>
                            </Popover>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {row._employee_name || row.employee_code}
                        </td>
                        <td className="px-3 py-2 capitalize">{row.leave_type}</td>
                        <td className="px-3 py-2">
                          {row.start_date} - {row.end_date}
                        </td>
                        <td className="px-3 py-2 truncate max-w-32">{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onBulkImport} 
            disabled={importing || importPreview.filter(r => r._valid).length === 0}
            className="gap-2"
          >
            {importing ? (
              <>
                <div className="spinner w-4 h-4"></div>
                Importing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Import {importPreview.filter(r => r._valid).length} Records
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImportModal;
